import { useEffect, useState } from "react";
import { Sparkle } from "@phosphor-icons/react";
import { api, act, useFetch, fmtDate } from "@/lib/api";
import { Panel, Field, Empty, Badge } from "@/components/common";
import { Sel } from "@/components/Sel";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StyleToggle } from "@/components/studio/kit";
import { NewsletterEditor } from "@/components/studio/NewsletterEditor";
import { SeriesPanel } from "@/components/studio/SeriesPanel";

export const NewsletterMaker = () => {
  const { data: products } = useFetch("/products");
  const { data: segs } = useFetch("/segments");
  const { data: list, reload } = useFetch("/studio/newsletters");
  const approved = (products || []).filter((p) => p.status === "approved");
  const [f, setF] = useState({ topic: "", style: "photo", with_image: true });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const set = (k, v) => setF((o) => ({ ...o, [k]: v }));
  useEffect(() => { if (!f.product_id && approved[0]) set("product_id", approved[0].id); }, [approved.length]); // eslint-disable-line
  useEffect(() => { if (!f.segment_id && segs?.[0]) set("segment_id", segs[0].id); }, [segs]); // eslint-disable-line
  const go = async () => {
    setBusy(true);
    const r = await act(() => api.post("/studio/newsletters", f), "Newsletter drafted — hero image generating");
    setBusy(false);
    if (r) { set("topic", ""); reload(); setOpen(r.id); }
  };
  return (
    <div className="grid xl:grid-cols-[380px_1fr] gap-4">
      <Panel title="New newsletter" className="self-start">
        <div className="space-y-4">
          <Field label="Product (approved claims only)"><Sel testid="nl-product-select" value={f.product_id} onChange={(v) => set("product_id", v)} options={approved.map((p) => ({ value: p.id, label: p.name }))} placeholder="Approve a product first" /></Field>
          <Field label="Audience segment" hint="Only contacts with recorded email permission receive it"><Sel testid="nl-segment-select" value={f.segment_id} onChange={(v) => set("segment_id", v)} options={(segs || []).map((s) => ({ value: s.id, label: s.name }))} /></Field>
          <Field label="Topic / angle"><Textarea rows={3} data-testid="nl-topic-input" value={f.topic} onChange={(e) => set("topic", e.target.value)} placeholder="e.g. 5 ways firms get client documents in before EOFY" className="bg-black text-sm" /></Field>
          <Field label="Hero image look"><StyleToggle value={f.style} onChange={(v) => set("style", v)} testid="nl-style" /></Field>
          <Button data-testid="nl-generate-btn" disabled={busy || !f.product_id || !f.segment_id || f.topic.trim().length < 6} onClick={go} className="w-full gap-2 bg-[#007AFF] hover:bg-[#3395FF]"><Sparkle weight="fill" /> {busy ? "Writing…" : "Draft newsletter"}</Button>
        </div>
      </Panel>
      <div className="space-y-3">
      <SeriesPanel products={approved} segs={segs} />
      <div className="space-y-2">{!list?.length ? <Empty>No newsletters yet.</Empty> : list.map((n) => (
        <button key={n.id} data-testid={`nl-row-${n.id}`} onClick={() => setOpen(n.id)} className="w-full text-left border border-border rounded-md bg-[#121212] hover:border-zinc-500 transition-colors px-4 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">{n.content?.subject}</div><div className="text-xs text-zinc-500 truncate">{n.topic} · {fmtDate(n.created_at)}{n.scheduled_at && ` · sends ${fmtDate(n.scheduled_at)}`}</div></div>
          {n.series_id && <Badge v="series" tone="blue" />}
          {n.send_result && <span className="text-[11px] font-mono text-zinc-400">{n.send_result.mode}: sent {n.send_result.sent} · test {n.send_result.test_redirected} · blocked {n.send_result.blocked}</span>}
          <Badge v={n.status} />
        </button>
      ))}</div>
      </div>
      <NewsletterEditor id={open} onClose={() => setOpen(null)} onChanged={reload} />
    </div>
  );
};
